<template v-if="displaySectionSummary">
    <template v-if="isSmallSummary">
        <template v-for="(field, stepName) in formatedSectionFields" :key="stepName">
            <template v-if="displayValue(field?.value, field?.refs?.formatterOnSummary)">
                <SmallSummaryField
                    :label="$t(`form.summary.labels.${stepName}`)"
                    :value="
                        stepName === 'chequingAccount'
                            ? bankAccountType(field?.value)
                            : displayValue(field?.value, field?.refs?.formatterOnSummary)
                    "
                    :has-error="!isFieldValid(field)"
                    @edit="edit(field)"
                />
            </template>
        </template>
    </template>

    <div v-else class="summary-section">
        <div class="row align-items-center">
            <div class="col-12">
                <h2>{{ sectionTitle }}</h2>
            </div>

            <template v-for="(field, stepName) in formatedSectionFields" :key="stepName">
                <template v-if="displayValue(field?.value, field?.refs?.formatterOnSummary)">
                    <FullSummaryField
                        :label="$t(`form.fields.${stepName}.label`, this.shortCodeList)"
                        :value="
                            stepName === 'chequingAccount'
                                ? bankAccountType(field?.value)
                                : displayValue(field?.value, field?.refs?.formatterOnSummary)
                        "
                        :has-error="!isFieldValid(field)"
                        @edit="edit(field)"
                    />
                </template>
            </template>
        </div>
    </div>
</template>

<script>
import { useStore } from 'vuex'
import { useRouter } from 'vue-router'
import { shortCodes } from '@js/utilities//language.js'
import { format } from '@js/utilities/formatter.js'
import { useField } from 'vee-validate'

// lodash components
import find from 'lodash/find'
import includes from 'lodash/includes'
import some from 'lodash/some'
import size from 'lodash/size'
import omit from 'lodash/omit'

export default {
    setup(props) {
        const store = useStore()
        const router = useRouter()
        return {
            store,
            router,
        }
    },
    data() {
        return {
            invalid: false,
        }
    },
    props: {
        sectionSteps: null,
        sectionTitle: null,
        isSmallSummary: false,
    },
    mounted() {
        this.validateFields(this.fields)
    },
    computed: {
        shortCodeList() {
            return shortCodes(this.store)
        },
        fields() {
            const fields = {}
            for (const step of this.sectionSteps) {
                if (!step.hideOnSummary) {
                    for (const fieldName in step.fields) {
                        const field = step.fields[fieldName]
                        if (!field.hideOnSummary) {
                            fields[fieldName] = field
                        }
                    }
                }
            }
            return fields
        },
        formatedSectionFields() {
            let formatedSectionFields = {}
            const addressSteps = ['previousAddress', 'currentAddress', 'coBorrowerAddress']
            for (const step of this.sectionSteps) {
                if (includes(addressSteps, step.key)) {
                    step.manual = true
                }

                if (!step.hideOnSummary) {
                    if (step.stringOnSummary) {
                        formatedSectionFields[step.key] = {
                            value: this.stringifyFields(step.fields),
                            step: step,
                            refs: step.fields,
                        }
                    } else {
                        for (const fieldName in step.fields) {
                            const field = step.fields[fieldName]
                            if (!field.hideOnSummary) {
                                let value = this.formattedValue(field, fieldName)
                                formatedSectionFields[fieldName] = {
                                    value: value,
                                    step: step,
                                    refs: field,
                                }
                            }
                        }
                    }
                }
            }

            return formatedSectionFields
        },
        displaySectionSummary() {
            let sectionTitleFlag = false
            for (const fieldName in this.formatedSectionFields) {
                const field = this.formatedSectionFields[fieldName]
                if (this.displayValue(field?.value, field?.refs?.formatterOnSummary)) {
                    sectionTitleFlag = true
                    break
                }
            }

            return sectionTitleFlag
        },
    },
    methods: {
        async validateFields(fields) {
            // setup validation fields
            for (const fieldName in fields) {
                const field = fields[fieldName]

                if (size(field.validationRules) > 0) {
                    let validation = omit(field.validationRules, field?.removeSummaryValidation)
                    const { validate } = useField(fieldName, validation, {
                        initialValue: field.value,
                    })

                    // create promises on each field validation
                    field.valid = true //set initial value
                    field.validate = validate
                }
            }

            // validate after each field is setup else some fields dont validate correctly
            for (const fieldName in fields) {
                const field = fields[fieldName]

                if (field.validate) {
                    const validationResult = await field.validate()
                    field.valid = validationResult.valid
                }
            }
        },
        async isFieldValid(field) {
            // TODO: this could be removed but we need to check all use cases
            if (field?.value === null) {
                return true
            }

            let valid = false
            if (field.refs.valid === undefined) {
                valid = !some(field.refs, ['valid', false])
            } else {
                valid = field.refs.valid
            }

            await this.store.dispatch('markStepComplete', {
                step: field.step,
                completed: valid,
            })

            return valid
        },
        formattedValue(field, fieldName) {
            let options = null

            try {
                options = this.$i18n.messages[this.$i18n.locale].form.fields[fieldName].options
            } catch (e) {
                console.error(
                    fieldName,
                    `missing options in translation`,
                    this.$i18n.messages[this.$i18n.locale].form.fields[fieldName],
                )
            }

            if (options) {
                let selectedOption = find(options, {
                    value: parseInt(field.value),
                })

                // test for string if integer fails
                if (selectedOption === undefined) {
                    selectedOption = find(options, {
                        value: field.value,
                    })
                }

                // test for id if all above fails
                if (selectedOption === undefined) {
                    selectedOption = find(options, {
                        id: parseInt(field.value),
                    })
                }

                return selectedOption
                    ? selectedOption.name
                    : this.displayValue(field.value, field?.step?.formatterOnSummary)
            }

            return field.value
        },
        stringifyFields(stepFields) {
            let stringifyFields = null
            for (const fieldName in stepFields) {
                const field = stepFields[fieldName]
                if (!field.hideOnSummary && field.value !== null) {
                    stringifyFields = stringifyFields ? `${stringifyFields}, ${field.value}` : field.value
                }
            }
            return stringifyFields
        },
        edit(field) {
            this.store.state.apply.backToStep = this.store.state.apply.currentRouteStep
            this.$router.push({ name: field.step.route.name })
        },
        displayValue(fieldValue, formatter) {
            return formatter ? format(fieldValue, formatter) : fieldValue
        },
        bankAccountType(fieldValue) {
            if (parseInt(fieldValue) === 1) {
                return this.$t('form.sections.aboutYou.chequingAccount.checking')
            }
            return this.$t('form.sections.aboutYou.chequingAccount.savings')
        },
    },
}
</script>

<style lang="scss">
@import '~scss/settings/responsive';
@import '~scss/settings/variables';
@import '~scss/mixins/rem';

.summary-section {
    background: #f4f5f6;
    border-radius: 5px;
    margin-bottom: 4px;
    padding: 17px;

    h2 {
        font-size: rem(18px);
        font-family: var(--heading-font);
        font-weight: 700;
        margin: 0;
    }
}
</style>
